import { awsCronToHumanReadable } from './HumanReadableCron';

/**
 * AWS EventBridge Cron Engine (JavaScript)
 *
 * Format:
 * cron(Minutes Hours Day-of-month Month Day-of-week Year)
 *
 * Notes:
 * - Quartz cron (NOT Linux cron)
 * - One of Day-of-month or Day-of-week must be '?'
 */

/* =======================
   Public API
======================= */

export function buildEventBridgeCron(state) {
  switch (state.selectedFrequency) {
    case 'hourly':
      return buildHourlyCron(state);
    case 'daily':
      return buildDailyCron(state);
    case 'weekly':
      return buildWeeklyCron(state);
    case 'monthly':
      return buildMonthlyCron(state);
    case 'cron':
      return `cron(${state.expression})`;
    default:
      throw new Error('Unsupported frequency');
  }
}

export function getHumanReadableExplanation(state) {
  const cronExpression = buildEventBridgeCron(state);
  const cronWithoutWrapper = cronExpression.replace('cron(', '').replace(')', '');
  return awsCronToHumanReadable(cronWithoutWrapper);
}

export function getNextRunPreviews(state, count = 10) {
  if (state.selectedFrequency === 'cron') {
    return getCronNextRunPreviews(state.expression, count);
  }
  
  const now = new Date();
  const previews = [];
  
  for (let i = 0; i < count; i++) {
    const nextRun = calculateNextRun(state, now, i);
    if (nextRun) {
      previews.push(nextRun.toLocaleString());
    }
  }
  
  return previews;
}

export function calculateDailyScheduleCount(frequency, config) {
  try {
    const cronExpression = buildEventBridgeCron({
      selectedFrequency: frequency,
      ...config
    });
    
    // Parse cron: minute hour day month dayOfWeek year
    const cronParts = cronExpression.replace('cron(', '').replace(')', '').split(' ');
    const [minute, hour, day, month, dayOfWeek, year] = cronParts;
    
    // Count executions per day based on cron parts
    let dailyCount = 0;
    
    // Count hours (how many times per day)
    if (hour === '*') {
      dailyCount = 24; // Every hour
    } else if (hour.includes('/')) {
      const interval = parseInt(hour.split('/')[1]);
      dailyCount = Math.floor(24 / interval);
    } else if (hour.includes(',')) {
      dailyCount = hour.split(',').length;
    } else if (hour.includes('-')) {
      const [start, end] = hour.split('-').map(Number);
      dailyCount = end - start + 1;
    } else {
      dailyCount = 1; // Specific hour
    }
    
    // Adjust for minute frequency within the hour
    if (minute === '*') {
      dailyCount *= 60; // Every minute
    } else if (minute.includes('/')) {
      const interval = parseInt(minute.split('/')[1]);
      dailyCount *= Math.floor(60 / interval);
    } else if (minute.includes(',')) {
      dailyCount *= minute.split(',').length;
    } else if (minute.includes('-')) {
      const [start, end] = minute.split('-').map(Number);
      dailyCount *= (end - start + 1);
    } else {
      // Specific minute (e.g., "2") - runs once per hour
      dailyCount *= 1;
    }
    
    return dailyCount;
    
  } catch (error) {
    console.error('Error calculating daily schedule count:', error);
    // Fallback to simple frequency-based calculation
    switch (frequency) {
      case 'hourly': return 24;
      case 'daily': return 1;
      case 'weekly': return 1;
      case 'monthly': return 1;
      default: return 1;
    }
  }
}

/* =======================
   Hourly
======================= */

function buildHourlyCron(state) {
  const minute = state.specificMinute || '0';
  let hour;

  switch (state.option) {
    case 'interval':
      hour = `*/${state.interval}`;
      break;

    case 'specific':
      if (!state.selectedHours || state.selectedHours.length === 0) {
        throw new Error('At least one hour must be selected');
      }
      hour = state.selectedHours.join(',');
      break;

    case 'range':
      hour = `${state.rangeStart}-${state.rangeEnd}`;
      break;

    default:
      throw new Error('Invalid hourly option');
  }

  return formatCron(minute, hour, '*', '*', '?', '*');
}

/* =======================
   Daily
======================= */

function buildDailyCron(state) {
  // Handle combined daily + hourly configuration
  if (state.hourlyConfig) {
    const minute = state.hourlyConfig.specificMinute || '0';
    let hour;
    
    // Use hourly configuration for hour field
    switch (state.hourlyConfig.option) {
      case 'interval':
        hour = `*/${state.hourlyConfig.interval}`;
        break;
      case 'specific':
        if (state.hourlyConfig.selectedHours && state.hourlyConfig.selectedHours.length > 0) {
          hour = state.hourlyConfig.selectedHours.join(',');
        } else {
          hour = '0';
        }
        break;
      case 'range':
        hour = `${state.hourlyConfig.rangeStart}-${state.hourlyConfig.rangeEnd}`;
        break;
      default:
        hour = '0';
    }
    
    // Apply daily configuration to day fields
    if (state.selectedDays && state.selectedDays.length > 0) {
      return formatCron(minute, hour, '?', '*', state.selectedDays.join(','), '*');
    }
    
    if (state.everyXDays && state.calendarDay) {
      const dom = `${state.calendarDayBegin}-${state.calendarDayEnd}/${state.everyXDaysValue}`;
      return formatCron(minute, hour, dom, '*', '?', '*');
    }
    
    if (state.everyXDays) {
      return formatCron(minute, hour, `*/${state.everyXDaysValue}`, '*', '?', '*');
    }
    
    if (state.workingDay) {
      return formatCron(minute, hour, '?', '*', 'MON-FRI', '*');
    }
    
    if (state.calendarDay) {
      const dom = `${state.calendarDayBegin}-${state.calendarDayEnd}`;
      return formatCron(minute, hour, dom, '*', '?', '*');
    }
    
    // Default daily with hourly
    return formatCron(minute, hour, '*', '*', '?', '*');
  }
  
  // Original daily logic (runs at midnight)
  const minute = '0';
  const hour = '0';

  // If specific days are selected, use day-of-week
  if (state.selectedDays && state.selectedDays.length > 0) {
    return formatCron(minute, hour, '?', '*', state.selectedDays.join(','), '*');
  }

  // Combination: Every X days + Calendar day range
  if (state.everyXDays && state.calendarDay) {
    const dom = `${state.calendarDayBegin}-${state.calendarDayEnd}/${state.everyXDaysValue}`;
    return formatCron(minute, hour, dom, '*', '?', '*');
  }

  // Every X days only
  if (state.everyXDays) {
    return formatCron(
      minute,
      hour,
      `*/${state.everyXDaysValue}`,
      '*',
      '?',
      '*'
    );
  }

  // Working days (Mon–Fri approximation)
  if (state.workingDay) {
    return formatCron(minute, hour, '?', '*', 'MON-FRI', '*');
  }

  // Calendar day range only
  if (state.calendarDay) {
    const dom = `${state.calendarDayBegin}-${state.calendarDayEnd}`;
    return formatCron(minute, hour, dom, '*', '?', '*');
  }

  throw new Error('No daily configuration selected');
}

/* =======================
   Weekly
======================= */

function buildWeeklyCron(state) {
  // Handle combined weekly + hourly configuration
  const minute = state.hourlyConfig ? (state.hourlyConfig.specificMinute || '0') : '0';
  let hour = '0';
  
  if (state.hourlyConfig) {
    switch (state.hourlyConfig.option) {
      case 'interval':
        hour = `*/${state.hourlyConfig.interval}`;
        break;
      case 'specific':
        if (state.hourlyConfig.selectedHours && state.hourlyConfig.selectedHours.length > 0) {
          hour = state.hourlyConfig.selectedHours.join(',');
        }
        break;
      case 'range':
        hour = `${state.hourlyConfig.rangeStart}-${state.hourlyConfig.rangeEnd}`;
        break;
    }
  }

  const days = [];
  if (state.days?.monday) days.push('MON');
  if (state.days?.tuesday) days.push('TUE');
  if (state.days?.wednesday) days.push('WED');
  if (state.days?.thursday) days.push('THU');
  if (state.days?.friday) days.push('FRI');
  if (state.days?.saturday) days.push('SAT');
  if (state.days?.sunday) days.push('SUN');

  if (days.length === 0) {
    throw new Error('At least one weekday must be selected');
  }

  return formatCron(minute, hour, '?', '*', days.join(','), '*');
}

/* =======================
   Monthly
======================= */

function buildMonthlyCron(state) {
  // Handle combined monthly + hourly configuration
  const minute = state.hourlyConfig ? (state.hourlyConfig.specificMinute || '0') : '0';
  let hour = '0';
  
  if (state.hourlyConfig) {
    switch (state.hourlyConfig.option) {
      case 'interval':
        hour = `*/${state.hourlyConfig.interval}`;
        break;
      case 'specific':
        if (state.hourlyConfig.selectedHours && state.hourlyConfig.selectedHours.length > 0) {
          hour = state.hourlyConfig.selectedHours.join(',');
        }
        break;
      case 'range':
        hour = `${state.hourlyConfig.rangeStart}-${state.hourlyConfig.rangeEnd}`;
        break;
    }
  }

  // Calendar day every N months
  if (state.calendarDay) {
    return formatCron(
      minute,
      hour,
      state.calendarDayValue,
      `*/${state.calendarDayMonths}`,
      '?',
      '*'
    );
  }

  // Working days
  if (state.workingDay) {
    return formatCron(
      minute,
      hour,
      '?',
      `*/${state.workingDayMonths}`,
      'MON-FRI',
      '*'
    );
  }

  throw new Error('Invalid monthly configuration');
}

/* =======================
   Helpers
======================= */

/* =======================
   Human-readable explanations
======================= */

function getHourlyExplanation(state) {
  switch (state.option) {
    case 'interval':
      return `Every ${state.interval} hour(s)`;
    case 'specific':
      const hours = state.selectedHours.map(h => `${h.toString().padStart(2, '0')}:00`).join(', ');
      return `At ${hours} daily`;
    case 'range':
      return `Every hour from ${state.rangeStart.padStart(2, '0')}:00 to ${state.rangeEnd.padStart(2, '0')}:00`;
    default:
      return 'Hourly';
  }
}

function getDailyExplanation(state) {
  if (state.selectedDays && state.selectedDays.length > 0) {
    const dayNames = {
      'MON': 'Monday', 'TUE': 'Tuesday', 'WED': 'Wednesday', 'THU': 'Thursday',
      'FRI': 'Friday', 'SAT': 'Saturday', 'SUN': 'Sunday'
    };
    const days = state.selectedDays.map(day => dayNames[day]).join(', ');
    return `On ${days} at 12:00 AM`;
  }
  if (state.everyXDays && state.calendarDay) {
    return `Every ${state.everyXDaysValue} day(s) from day ${state.calendarDayBegin} to ${state.calendarDayEnd} at 12:00 AM`;
  }
  if (state.everyXDays) {
    return `Every ${state.everyXDaysValue} day(s) at 12:00 AM`;
  }
  if (state.workingDay) {
    return 'Every weekday at 12:00 AM';
  }
  if (state.calendarDay) {
    return `Daily from day ${state.calendarDayBegin} to ${state.calendarDayEnd} at 12:00 AM`;
  }
  return 'Daily at 12:00 AM';
}

function getWeeklyExplanation(state) {
  const days = [];
  if (state.days?.monday) days.push('Monday');
  if (state.days?.tuesday) days.push('Tuesday');
  if (state.days?.wednesday) days.push('Wednesday');
  if (state.days?.thursday) days.push('Thursday');
  if (state.days?.friday) days.push('Friday');
  if (state.days?.saturday) days.push('Saturday');
  if (state.days?.sunday) days.push('Sunday');
  
  const frequency = state.everyXWeeks > 1 ? `every ${state.everyXWeeks} weeks` : 'weekly';
  return `${frequency} on ${days.join(', ')} at 12:00 AM`;
}

function getMonthlyExplanation(state) {
  if (state.calendarDay) {
    return `On day ${state.calendarDayValue} every ${state.calendarDayMonths} month(s) at 12:00 AM`;
  }
  if (state.workingDay) {
    return `On weekdays every ${state.workingDayMonths} month(s) at 12:00 AM`;
  }
  return 'Monthly at 12:00 AM';
}

/* =======================
   Next run calculations
======================= */

function calculateNextRun(state, baseDate, offset) {
  const date = new Date(baseDate);
  
  switch (state.selectedFrequency) {
    case 'hourly':
      return calculateHourlyNextRun(state, date, offset);
    case 'daily':
      date.setDate(date.getDate() + offset);
      date.setHours(0, 0, 0, 0);
      return date;
    case 'weekly':
      return calculateWeeklyNextRun(state, date, offset);
    case 'monthly':
      date.setMonth(date.getMonth() + offset);
      date.setDate(parseInt(state.calendarDayValue || 1));
      date.setHours(0, 0, 0, 0);
      return date;
    default:
      return null;
  }
}

function calculateHourlyNextRun(state, date, offset) {
  switch (state.option) {
    case 'interval':
      const interval = parseInt(state.interval);
      date.setHours(date.getHours() + (offset * interval));
      return date;
    case 'specific':
      if (state.selectedHours && state.selectedHours.length > 0) {
        const hourIndex = offset % state.selectedHours.length;
        const dayOffset = Math.floor(offset / state.selectedHours.length);
        date.setDate(date.getDate() + dayOffset);
        date.setHours(parseInt(state.selectedHours[hourIndex]), 0, 0, 0);
        return date;
      }
      break;
    case 'range':
      const start = parseInt(state.rangeStart);
      const end = parseInt(state.rangeEnd);
      const rangeSize = end - start + 1;
      const hourInRange = offset % rangeSize;
      const dayOffset = Math.floor(offset / rangeSize);
      date.setDate(date.getDate() + dayOffset);
      date.setHours(start + hourInRange, 0, 0, 0);
      return date;
  }
  return date;
}

function calculateWeeklyNextRun(state, date, offset) {
  const days = [];
  if (state.days?.monday) days.push(1);
  if (state.days?.tuesday) days.push(2);
  if (state.days?.wednesday) days.push(3);
  if (state.days?.thursday) days.push(4);
  if (state.days?.friday) days.push(5);
  if (state.days?.saturday) days.push(6);
  if (state.days?.sunday) days.push(0);
  
  if (days.length === 0) return null;
  
  const dayIndex = offset % days.length;
  const weekOffset = Math.floor(offset / days.length) * (state.everyXWeeks || 1);
  
  date.setDate(date.getDate() + (weekOffset * 7));
  const currentDay = date.getDay();
  const targetDay = days[dayIndex];
  const daysToAdd = (targetDay - currentDay + 7) % 7;
  date.setDate(date.getDate() + daysToAdd);
  date.setHours(0, 0, 0, 0);
  
  return date;
}

function formatCron(minute, hour, dayOfMonth, month, dayOfWeek, year) {
  return `cron(${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek} ${year})`;
}

function getCronNextRunPreviews(cronExpression, count = 10) {
  if (!cronExpression || cronExpression.trim() === '') {
    return ['Enter a cron expression to see preview'];
  }

  try {
    const parts = cronExpression.trim().split(' ');
    if (parts.length !== 6) {
      return ['Invalid cron format. Expected: minute hour day month dayOfWeek year'];
    }

    const [minute, hour, day, month, dayOfWeek, year] = parts;
    const now = new Date();
    const previews = [];

    // Simple preview generation for common patterns
    for (let i = 0; i < count; i++) {
      const nextRun = calculateCronNextRun(parts, now, i);
      if (nextRun) {
        previews.push(nextRun.toLocaleString());
      }
    }

    return previews.length > 0 ? previews : ['Unable to calculate preview for this cron expression'];
  } catch (error) {
    return ['Invalid cron expression'];
  }
}

function calculateCronNextRun(cronParts, baseDate, offset) {
  const [minute, hour, day, month, dayOfWeek, year] = cronParts;
  const date = new Date(baseDate);
  
  // Add offset days for simple calculation
  date.setDate(date.getDate() + offset);
  
  // Set specific hour if provided
  if (hour !== '*' && !hour.includes('/') && !hour.includes(',')) {
    date.setHours(parseInt(hour) || 0);
  }
  
  // Set specific minute if provided
  if (minute !== '*' && !minute.includes('/') && !minute.includes(',')) {
    date.setMinutes(parseInt(minute) || 0);
  }
  
  date.setSeconds(0);
  date.setMilliseconds(0);
  
  return date;
}
